import { EChartsOption } from 'echarts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getSales } from '@/api/clarityLens'
import { chartColors } from '@/constants/COLOR_PALETTE'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import {
  SalesModel,
  SeasonalityModel,
  SeasonalityResponseModel,
  StoreBreakdownModel,
  StoreBreakdownResponseModel,
  TimeOfDayBreakdownModel,
  TimeOfDayBreakdownResponseModel,
} from '@/types/clarityLens'

type ChartDataModel = {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}

type BarChartDataModel = {
  chartOptions: EChartsOption
  data: StoreBreakdownModel[]
}

type LineChartDataModel = {
  chartOptions: EChartsOption
  data: SeasonalityModel
}

export default function useSales(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string,
  size: string,
  temperature: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemGroupName?.trim()),
    [startDate, endDate, branches, itemGroupName]
  )
  const loading = !isReady || isFetching

  const [timeOfDayBreakdownChartData, setTimeOfDayBreakdownChartData] =
    useState<ChartDataModel>()
  const [storeBreakdownChartData, setStoreBreakdownChartData] =
    useState<BarChartDataModel>()
  const [seasonalityChartData, setSeasonalityChartData] =
    useState<LineChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const { data } = await fetchSalesData(
        branches,
        startDate,
        endDate,
        itemGroupName,
        size,
        temperature
      )

      // Convert sales data to chart formats
      const chartDataFormatted: ChartDataModel = convertToStackedBarFormat(data)
      setTimeOfDayBreakdownChartData(chartDataFormatted)

      // Convert store breakdown to bar chart
      const barChart: BarChartDataModel = convertToBarChart(data.storeBreakdown)
      setStoreBreakdownChartData(barChart)

      // Convert seasonality to line chart
      const lineChart: LineChartDataModel = convertToLineChart(data.seasonality)
      setSeasonalityChartData(lineChart)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemGroupName, size, temperature])

  const refetch = useCallback(() => {
    console.log('Refetching sales data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    timeOfDayBreakdownChartData,
    storeBreakdownChartData,
    seasonalityChartData,
    loading,
    refetch,
  }
}

async function fetchSalesData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string,
  size: string,
  temperature: string
): Promise<{
  data: SalesModel
}> {
  try {
    const response = await getSales(
      branches,
      startDate,
      endDate,
      itemGroupName,
      size,
      temperature
    )

    const data: SalesModel = convertData(
      response.time_of_day_breakdown,
      response.store_breakdown,
      response.seasonality
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  timeOfDayBreakdown: TimeOfDayBreakdownResponseModel[],
  storeBreakdown: StoreBreakdownResponseModel[],
  seasonality: SeasonalityResponseModel
): SalesModel {
  const timeOfDayBreakdownData: TimeOfDayBreakdownModel[] =
    timeOfDayBreakdown.map((item) => {
      return {
        day: item.day,
        timePeriods: item.time_periods,
      }
    })

  const storeBreakdownData: StoreBreakdownModel[] = storeBreakdown.map(
    (item) => {
      return {
        branchName: item.branch_name,
        grossSalesAmount: item.gross_sales_amount,
      }
    }
  )

  const seasonalityData: SeasonalityModel = {}
  Object.keys(seasonality).forEach((key) => {
    seasonalityData[key] = seasonality[key].map((item) => ({
      month: item.month,
      grossSalesAmount: item.gross_sales_amount,
    }))
  })

  return {
    timeOfDayBreakdown: timeOfDayBreakdownData,
    storeBreakdown: storeBreakdownData,
    seasonality: seasonalityData,
  }
}

function convertToStackedBarFormat(salesData: SalesModel): ChartDataModel {
  // Convert time of day breakdown to chart format
  const days = salesData.timeOfDayBreakdown.map((item) => item.day)
  const timePeriods =
    salesData.timeOfDayBreakdown.length > 0
      ? Object.keys(salesData.timeOfDayBreakdown[0].timePeriods)
      : []

  const rawData: number[][] = timePeriods.map((period) =>
    salesData.timeOfDayBreakdown.map(
      (dayData) => dayData.timePeriods[period] || 0
    )
  )

  const seriesNames: string[] = timePeriods.map((period) =>
    period.toUpperCase()
  )
  const xAxisNames: string[] = days

  return {
    rawData,
    seriesNames,
    xAxisNames,
  }
}

// Convert store breakdown data to bar chart format
function convertToBarChart(
  storeBreakdown: StoreBreakdownModel[]
): BarChartDataModel {
  const branchNames = storeBreakdown.map((item) => item.branchName)
  const salesAmounts = storeBreakdown.map((item) => item.grossSalesAmount)

  const chartOptions: EChartsOption = {
    grid: {
      top: '10%',
      bottom: '20%',
      left: '12%',
      right: '4%',
    },
    xAxis: {
      type: 'category',
      data: branchNames,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        rotate: branchNames.length > 5 ? 45 : 0,
        interval: 0,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '${value}',
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        return `${params.name}<br/>Sales: $${params.value.toLocaleString()}`
      },
    },
    series: [
      {
        type: 'bar' as const,
        data: salesAmounts,
        itemStyle: {
          color: chartColors[0],
          borderRadius: 6,
          borderWidth: 1,
          borderColor: '#fff',
        },
        barWidth: '60%',
        label: {
          show: true,
          position: 'top' as const,
          formatter: (params: any) => `$${params.value.toLocaleString()}`,
          color: '#14181f',
          fontSize: 12,
          fontWeight: 'bold',
        },
      },
    ],
  }

  return {
    chartOptions,
    data: storeBreakdown,
  }
}

// Convert seasonality data to line chart format
function convertToLineChart(seasonality: SeasonalityModel): LineChartDataModel {
  const periods = Object.keys(seasonality)
  const allMonths = new Set<string>()

  // Collect all unique branch names
  periods.forEach((period) => {
    seasonality[period].forEach((item) => {
      allMonths.add(item.month)
    })
  })

  const months = Array.from(allMonths)

  // Create series data for each month
  const series = months.map((month, index) => {
    const data = periods.map((period) => {
      const monthData = seasonality[period].find((item) => item.month === month)
      return monthData ? monthData.grossSalesAmount : 0
    })

    return {
      name: month,
      type: 'line' as const,
      data: data,
      itemStyle: {
        color: chartColors[index % chartColors.length],
      },
      lineStyle: {
        color: chartColors[index % chartColors.length],
        width: 2,
      },
      symbol: 'circle' as const,
      symbolSize: 6,
      smooth: true,
    }
  })

  const chartOptions: EChartsOption = {
    legend: {
      selectedMode: false,
      orient: 'horizontal',
      top: '0%',
      left: 'center',
      data: months,
      icon: 'circle',
      itemGap: 20,
    },
    grid: {
      bottom: '20%',
      left: '12%',
      right: '4%',
      top: '15%',
    },
    xAxis: {
      type: 'category',
      data: periods,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '${value}',
      },
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        let result = `${params[0].axisValue}<br/>`
        params.forEach((param: any) => {
          result += `${param.marker} ${param.seriesName}: $${param.value.toLocaleString()}<br/>`
        })
        return result
      },
    },
    series: series,
  }

  return {
    chartOptions,
    data: seasonality,
  }
}
